import { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { COUNTRIES } from "../data/countriesData";
import {
  BUREAU_CHANGE_FIELDS, BUREAU_CHANGE_FIELD_LABELS, BUREAU_CHANGE_STATUS_LABELS, fmtChangeDate,
  type BureauChangeField, type BureauChangeRequest, type BureauChangeStatus, type BureauDetails,
} from "../lib/bureauChangeRequests";

// Bureau Profile → Name and accreditation. The bureau's name, accreditation
// body, accreditation and country are what clients and invoices show, so a
// bureau administrator asks for a change and the platform owner decides it
// (backend/src/services/bureauChangeRequests.ts). One request awaits review at
// a time; the history keeps every outcome with the platform team's note.

interface PanelData {
  current: BureauDetails;
  pending: BureauChangeRequest | null;
  history: BureauChangeRequest[];
}

const sectionTitle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 };
const sectionCard: React.CSSProperties = { ...card, padding: 20 };
const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: theme.color.text, marginBottom: 6 };

export function statusChipStyle(status: BureauChangeStatus): React.CSSProperties {
  const map: Record<BureauChangeStatus, { color: string; bg: string }> = {
    pending:   { color: theme.color.warningText, bg: theme.color.warningBg },
    approved:  { color: theme.color.successText, bg: theme.color.successBg },
    declined:  { color: theme.color.dangerText,  bg: theme.color.dangerBg },
    withdrawn: { color: theme.color.muted,       bg: theme.color.panelAlt },
  };
  const s = map[status];
  return { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 3, background: s.bg, color: s.color, whiteSpace: "nowrap" };
}

/** "Name: Old → New" lines for the fields a request changes. */
export function ChangeLines({ request, current }: { request: Pick<BureauChangeRequest, "requested" | "previous">; current?: Partial<BureauDetails> }) {
  const fields = BUREAU_CHANGE_FIELDS.filter((f) => f in request.requested);
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
      {fields.map((f) => {
        const before = current ? current[f] ?? null : request.previous[f] ?? null;
        const after = request.requested[f] ?? null;
        return (
          <li key={f} style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.5, wordBreak: "break-word" }}>
            <span style={{ color: theme.color.subtle }}>{BUREAU_CHANGE_FIELD_LABELS[f]}: </span>
            <span style={{ color: before ? theme.color.muted : theme.color.faint, textDecoration: before ? "line-through" : undefined }}>{before || "(not set)"}</span>
            {" → "}
            <strong style={{ fontWeight: 600, color: after ? theme.color.heading : theme.color.faint }}>{after || "(cleared)"}</strong>
          </li>
        );
      })}
    </ul>
  );
}

export function BureauChangeRequestPanel() {
  const [data, setData] = useState<PanelData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<Record<BureauChangeField, string> | null>(null);
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<PanelData>("/auditor/bureau/change-requests")
      .then((d) => { setData(d); setLoadError(null); })
      .catch((e: unknown) => setLoadError(apiErrorMessage(e, "Failed to load the bureau's details.")));
  }, []);
  useEffect(load, [load]);

  const startRequest = () => {
    if (!data) return;
    setForm({
      name: data.current.name ?? "",
      accreditationBody: data.current.accreditationBody ?? "",
      accreditation: data.current.accreditation ?? "",
      country: data.current.country ?? "",
    });
    setReason(""); setError(null); setNotice(null); setOpen(true);
  };

  // Only the fields that differ from the bureau's present values are sent.
  const changedFields = data && form
    ? BUREAU_CHANGE_FIELDS.filter((f) => form[f].trim() !== (data.current[f] ?? ""))
    : [];

  const submit = async () => {
    if (!form || changedFields.length === 0) return;
    setBusy(true); setError(null);
    try {
      const body: Record<string, string | null> = { reason: reason.trim() };
      for (const f of changedFields) body[f] = form[f].trim() || null;
      await api.post("/auditor/bureau/change-requests", body);
      setOpen(false);
      setNotice("Request sent. The platform team will review it.");
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "The request could not be sent."));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id: string) => {
    if (!window.confirm("Withdraw this change request?")) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await api.post(`/auditor/bureau/change-requests/${id}/withdraw`, {});
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "The request could not be withdrawn."));
      load();
    } finally {
      setBusy(false);
    }
  };

  const value = (v: string | null) => (
    <div style={{ fontSize: 13, color: v ? theme.color.text : theme.color.faint, wordBreak: "break-word" }}>{v || "Not set"}</div>
  );

  return (
    <div style={sectionCard}>
      <div style={sectionTitle}>Name and accreditation</div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
        These details appear on client invoices, the marketplace and every engagement, so changes are reviewed by the platform team before they take effect.
      </div>

      {loadError && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{loadError}</div>}
      {!data && !loadError && <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>}

      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
            {BUREAU_CHANGE_FIELDS.map((f) => (
              <div key={f}>
                <div style={label}>{BUREAU_CHANGE_FIELD_LABELS[f]}</div>
                {value(data.current[f])}
              </div>
            ))}
          </div>

          {data.pending ? (
            <div style={{ border: `1px solid ${theme.color.warning}`, background: theme.color.warningBg, borderRadius: 3, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
                  Change awaiting review <span style={{ fontWeight: 400, color: theme.color.subtle }}>· asked {fmtChangeDate(data.pending.createdAt)}{data.pending.requestedBy ? ` by ${data.pending.requestedBy.displayName}` : ""}</span>
                </div>
                <button type="button" onClick={() => withdraw(data.pending!.id)} disabled={busy} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 12 }}>
                  Withdraw
                </button>
              </div>
              <ChangeLines request={data.pending} />
              <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 8, whiteSpace: "pre-wrap" }}>Reason: {data.pending.reason}</div>
            </div>
          ) : open && form ? (
            <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                {BUREAU_CHANGE_FIELDS.map((f) => (
                  <label key={f} style={{ display: "block" }}>
                    <div style={label}>{BUREAU_CHANGE_FIELD_LABELS[f]}</div>
                    {f === "country" ? (
                      // The platform's own country list, as Operating Locations use (the API refuses anything else).
                      <select
                        value={form[f]}
                        onChange={(e) => setForm((p) => p ? { ...p, [f]: e.target.value } : p)}
                        style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                      >
                        <option value="">Not set</option>
                        {form[f] && !COUNTRIES.includes(form[f]) && <option value={form[f]}>{form[f]}</option>}
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <input
                        value={form[f]}
                        maxLength={f === "name" || f === "accreditation" ? 200 : 100}
                        required={(f === "accreditation" || f === "accreditationBody") && !!data?.current[f]}
                        onChange={(e) => setForm((p) => p ? { ...p, [f]: e.target.value } : p)}
                        style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                      />
                    )}
                  </label>
                ))}
              </div>
              <label style={{ display: "block" }}>
                <div style={label}>Reason for the change</div>
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={reason}
                  placeholder="For example: renamed after a merger; accreditation transferred to a new body. Say what evidence you can provide."
                  onChange={(e) => setReason(e.target.value)}
                  style={{ ...inputStyle, width: "100%", resize: "vertical", boxSizing: "border-box" }}
                />
              </label>
              {error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || changedFields.length === 0 || reason.trim().length < 3 || !form.name.trim()}
                  style={{ ...btnPrimary, opacity: busy || changedFields.length === 0 || reason.trim().length < 3 || !form.name.trim() ? 0.6 : 1 }}
                >
                  {busy ? "Sending…" : "Send for review"}
                </button>
                <button type="button" onClick={() => setOpen(false)} disabled={busy} style={btnSecondary}>Cancel</button>
                <span style={{ fontSize: 12, color: theme.color.subtle }}>
                  {changedFields.length === 0 ? "Change at least one field." : !form.name.trim() ? "A bureau needs a name." : `${changedFields.length} field${changedFields.length === 1 ? "" : "s"} will change.`}
                </span>
              </div>
            </div>
          ) : (
            <div>
              <button type="button" onClick={startRequest} style={btnPrimary}>Request a change</button>
            </div>
          )}

          {!open && error && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>}
          {notice && <div style={{ fontSize: 13, color: theme.color.successText }}>{notice}</div>}

          {data.history.length > 0 && (
            <div>
              <div style={{ ...label, marginBottom: 8 }}>Previous requests</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.history.map((r) => (
                  <div key={r.id} style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "10px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, color: theme.color.subtle }}>
                        Asked {fmtChangeDate(r.createdAt)}{r.requestedBy ? ` by ${r.requestedBy.displayName}` : ""}
                        {r.reviewedAt ? ` · decided ${fmtChangeDate(r.reviewedAt)}` : ""}
                      </span>
                      <span style={statusChipStyle(r.status)}>{BUREAU_CHANGE_STATUS_LABELS[r.status]}</span>
                    </div>
                    <ChangeLines request={r} />
                    {r.reviewNote && (
                      <div style={{ fontSize: 12, color: theme.color.text, marginTop: 6, whiteSpace: "pre-wrap" }}>
                        <span style={{ color: theme.color.subtle }}>Platform team: </span>{r.reviewNote}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
